import { useCallback, useEffect, useRef, useState } from 'react';
const CHANGE_EVENT = 'thousand-sunny:tablePreferencesChange';
const EMPTY = {
    sort: null,
    filters: {},
    view: 'table'
};
function read(storageKey, defaults) {
    if (typeof window === 'undefined') return defaults;
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (raw === null) return defaults;
        const parsed = JSON.parse(raw);
        // Merge over defaults so a partial/older blob (missing `view`, say) still
        // yields a complete, valid preferences object.
        return {
            sort: parsed.sort ?? defaults.sort,
            filters: parsed.filters ?? defaults.filters,
            view: parsed.view ?? defaults.view
        };
    } catch  {
        // Missing/corrupt/non-JSON value — fall back to defaults rather than throw.
        return defaults;
    }
}
function write(storageKey, value) {
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch  {
    // Storage unavailable (private mode, quota) — in-memory state still updates.
    }
    // Notify same-tab listeners; the native 'storage' event only fires in OTHER
    // tabs. detail.key lets unrelated tables skip the re-read.
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
        detail: {
            key: storageKey
        }
    }));
}
export function useTablePreferences(storageKey, defaults) {
    // Capture the caller's defaults ONCE. A call site that passes an inline
    // object would otherwise produce a new reference every render and churn every
    // memoized callback below; defaults are static per surface, so first-render
    // capture is both correct and stable.
    const baseRef = useRef(null);
    if (baseRef.current === null) baseRef.current = {
        ...EMPTY,
        ...defaults
    };
    const base = baseRef.current;
    const [prefs, setPrefs] = useState(()=>read(storageKey, base));
    useEffect(()=>{
        // Re-read on our own same-tab writes and on cross-tab storage events, both
        // filtered to this storage key so writes elsewhere don't churn this table.
        function handle(event) {
            if (event instanceof StorageEvent) {
                if (event.key !== null && event.key !== storageKey) return;
            } else if (event instanceof CustomEvent && event.detail?.key !== storageKey) {
                return;
            }
            setPrefs(read(storageKey, base));
        }
        window.addEventListener(CHANGE_EVENT, handle);
        window.addEventListener('storage', handle);
        return ()=>{
            window.removeEventListener(CHANGE_EVENT, handle);
            window.removeEventListener('storage', handle);
        };
    }, [
        storageKey,
        base
    ]);
    // Read the freshest stored value before each mutation so concurrent setters
    // compose instead of clobbering each other.
    const persist = useCallback((mutate)=>{
        const next = mutate(read(storageKey, base));
        setPrefs(next);
        write(storageKey, next);
    }, [
        storageKey,
        base
    ]);
    const setSort = useCallback((sort)=>persist((c)=>({
                ...c,
                sort
            })), [
        persist
    ]);
    const toggleSort = useCallback((key)=>persist((c)=>({
                ...c,
                sort: {
                    key,
                    dir: c.sort?.key === key && c.sort.dir === 'asc' ? 'desc' : 'asc'
                }
            })), [
        persist
    ]);
    const setFilter = useCallback((key, value)=>persist((c)=>{
            const filters = {
                ...c.filters
            };
            if (value === '') delete filters[key];
            else filters[key] = value;
            return {
                ...c,
                filters
            };
        }), [
        persist
    ]);
    const clearFilters = useCallback(()=>persist((c)=>({
                ...c,
                filters: {}
            })), [
        persist
    ]);
    const setView = useCallback((view)=>persist((c)=>({
                ...c,
                view
            })), [
        persist
    ]);
    return {
        ...prefs,
        setSort,
        toggleSort,
        setFilter,
        clearFilters,
        setView
    };
}
